/**
 * Stacking for stacked columns. Positive values stack upward from zero and negative values downward from zero, each in
 * series order, so a mix of signs never cancels. Gaps (`null`) stack as zero but are flagged.
 */
import { extent, isFiniteNumber } from './numbers.js';
/** Stack `series` over `length` categories (default: the longest series). */
export function stackSeries(series, length) {
    const n = length ?? series.reduce((m, s) => Math.max(m, s.values.length), 0);
    const up = new Array(n).fill(0);
    const down = new Array(n).fill(0);
    return series.map((s, index) => {
        const points = [];
        for (let i = 0; i < n; i += 1) {
            const raw = s.values[i];
            const gap = !isFiniteNumber(raw);
            const value = gap ? 0 : raw;
            if (value >= 0) {
                const y0 = up[i] ?? 0;
                points.push({ y0, y1: y0 + value, value, gap });
                up[i] = y0 + value;
            }
            else {
                const y1 = down[i] ?? 0;
                points.push({ y0: y1 + value, y1, value, gap });
                down[i] = y1 + value;
            }
        }
        return { key: s.key, index, points };
    });
}
/** The total height of each stacked column: positives plus the magnitude of negatives. */
export function stackTotals(layers, length) {
    const n = length ?? layers.reduce((m, l) => Math.max(m, l.points.length), 0);
    const out = new Array(n).fill(0);
    for (const layer of layers)
        layer.points.forEach((p, i) => (out[i] = (out[i] ?? 0) + Math.abs(p.value)));
    return out;
}
/** The value-axis extent of the stack (always includes zero), or `null` when there are no layers. */
export function stackExtent(layers) {
    const edges = layers.flatMap((l) => l.points.flatMap((p) => [p.y0, p.y1]));
    const e = extent(edges);
    if (e === null)
        return null;
    return { min: Math.min(0, e.min), max: Math.max(0, e.max) };
}
//# sourceMappingURL=stack.js.map